/**
 * F-604-FE-04: NotificationToast
 *
 * Real-time WebSocket notification toast.
 * Listens for `notification:new` messages via WebSocketContext.onMessage.
 * Toasts appear in the top-right corner, auto-dismiss after 5 seconds.
 * Multiple toasts stack vertically.
 * Clicking a toast performs the type-specific action.
 *
 * Must be rendered inside WebSocketProvider and Router (for navigate).
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWebSocket } from '@/contexts/WebSocketContext';
import type { NotificationType, NotificationMetadata } from '@/hooks/useNotifications';
import { useQueryClient } from '@tanstack/react-query';
import { NOTIFICATIONS_QUERY_KEY } from '@/hooks/useNotifications';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ToastItem {
  id: string;
  type: NotificationType;
  message: string;
  metadata: NotificationMetadata | null;
  createdAt: number;
}

// ---------------------------------------------------------------------------
// Toast icons (matching NotificationList)
// ---------------------------------------------------------------------------

function ToastIcon({ type }: { type: NotificationType }) {
  switch (type) {
    case 'NOTE_READY':
      return (
        <svg className="w-5 h-5 text-clinical-teal flex-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    case 'NOTE_FAILED':
      return (
        <svg className="w-5 h-5 text-red-500 flex-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    case 'PDF_READY':
      return (
        <svg className="w-5 h-5 text-clinical-charcoal flex-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      );
    case 'DRAFT_REMINDER':
      return (
        <svg className="w-5 h-5 text-clinical-amber flex-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    default:
      return (
        <svg className="w-5 h-5 text-clinical-teal flex-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
      );
  }
}

// ---------------------------------------------------------------------------
// Individual toast
// ---------------------------------------------------------------------------

interface SingleToastProps {
  toast: ToastItem;
  onDismiss: (id: string) => void;
  onAction: (toast: ToastItem) => void;
}

function SingleToast({ toast, onDismiss, onAction }: SingleToastProps) {
  return (
    <div
      className="
        flex items-start gap-3
        w-80 max-w-[calc(100vw-2rem)]
        bg-white rounded-clinical shadow-clinical-md border border-clinical-border
        px-4 py-3
        animate-slide-in-right
      "
      role="alert"
      aria-live="polite"
    >
      <div className="mt-0.5">
        <ToastIcon type={toast.type} />
      </div>

      <button
        type="button"
        className="flex-1 text-start focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-clinical-amber rounded"
        onClick={() => onAction(toast)}
      >
        <p className="text-sm font-body font-medium text-clinical-charcoal leading-snug">
          {toast.message}
        </p>
        <p className="text-xs font-body text-clinical-text-muted mt-0.5">
          Click to view
        </p>
      </button>

      <button
        type="button"
        onClick={() => onDismiss(toast.id)}
        className="
          inline-flex items-center justify-center flex-none
          w-6 h-6 rounded
          text-clinical-text-muted hover:text-clinical-charcoal hover:bg-clinical-cream
          transition-colors duration-150
          focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-clinical-amber
        "
        aria-label="Dismiss notification"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Toast container
// ---------------------------------------------------------------------------

export function NotificationToast() {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const navigate = useNavigate();
  const { onMessage } = useWebSocket();
  const queryClient = useQueryClient();
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  // -------------------------------------------------------------------------
  // Auto-dismiss after 5 seconds
  // -------------------------------------------------------------------------
  const scheduleAutoDismiss = useCallback((id: string) => {
    const timer = setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
      timersRef.current.delete(id);
    }, 5_000);
    timersRef.current.set(id, timer);
  }, []);

  const dismissToast = useCallback((id: string) => {
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  // -------------------------------------------------------------------------
  // Listen for notification:new WebSocket messages
  // -------------------------------------------------------------------------
  useEffect(() => {
    const unsubscribe = onMessage('notification:new', (msg) => {
      const toast: ToastItem = {
        id: `toast-${Date.now()}-${Math.random()}`,
        type: (msg['notificationType'] as NotificationType) ?? 'NOTE_READY',
        message: (msg['message'] as string) ?? 'New notification',
        metadata: (msg['metadata'] as NotificationMetadata | null) ?? null,
        createdAt: Date.now(),
      };

      setToasts((prev) => [...prev, toast]);
      scheduleAutoDismiss(toast.id);

      // Invalidate notifications query so badge updates immediately
      void queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY });
    });

    return unsubscribe;
  }, [onMessage, scheduleAutoDismiss, queryClient]);

  // -------------------------------------------------------------------------
  // Cleanup all timers on unmount
  // -------------------------------------------------------------------------
  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      timers.forEach((timer) => clearTimeout(timer));
      timers.clear();
    };
  }, []);

  // -------------------------------------------------------------------------
  // Type-specific action handler
  // -------------------------------------------------------------------------
  function handleAction(toast: ToastItem) {
    dismissToast(toast.id);

    switch (toast.type) {
      case 'NOTE_READY': {
        const noteId = toast.metadata?.noteId;
        navigate(noteId ? `/notes/${noteId}` : '/notes');
        break;
      }
      case 'NOTE_FAILED': {
        navigate('/notes');
        break;
      }
      case 'PDF_READY': {
        const downloadUrl = toast.metadata?.downloadUrl;
        if (downloadUrl) {
          window.open(downloadUrl, '_blank', 'noopener,noreferrer');
        }
        break;
      }
      case 'DRAFT_REMINDER': {
        const noteId = toast.metadata?.noteId;
        navigate(noteId ? `/notes/${noteId}` : '/notes');
        break;
      }
      default:
        navigate('/notes');
    }
  }

  if (toasts.length === 0) return null;

  return (
    <div
      className="fixed top-4 end-4 z-[9999] flex flex-col gap-2"
      aria-label="Toast notifications"
    >
      {toasts.map((toast) => (
        <SingleToast
          key={toast.id}
          toast={toast}
          onDismiss={dismissToast}
          onAction={handleAction}
        />
      ))}
    </div>
  );
}
